import { useAtom } from "jotai";
import { useEffect, useState } from "react";
import { transfersManagerAtom } from "@design-lab/entities/transfers-lab";
import { toFileObject } from "@design-lab/shared";
import { transfersBackgroundPreferenceAtom } from "../ui/tool-bar/TransfersRemoveBackground";

export function useTransfersArtboard() {
  const [state] = useAtom(transfersManagerAtom);
  const [backgroundPreference] = useAtom(transfersBackgroundPreferenceAtom);
  const [withBgImage, setWithBgImage] = useState<HTMLImageElement | null>(null);
  const [noBgImage, setNoBgImage] = useState<HTMLImageElement | null>(null);
  const { items, selectedId } = state.context;

  // Find selected item
  const selectedItem = items.find((item: any) => item.id === selectedId);
  const selectedSnapshot = selectedItem?.getSnapshot();

  // Extract artwork data
  const artwork = selectedSnapshot?.context.artwork;

  // Pre-load both image versions for seamless switching
  useEffect(() => {
    if (!artwork) {
      setWithBgImage(null);
      setNoBgImage(null);
      return;
    }

    // Load with background image
    if (artwork.withBgUrl) {
      const imgWithBg = new Image();
      imgWithBg.src = artwork.withBgUrl;
      imgWithBg.onload = () => setWithBgImage(imgWithBg);
      imgWithBg.onerror = () => {
        console.error("Failed to load background image");
        setWithBgImage(null);
      };
    }

    // Load without background image
    const noBgUrl = artwork.noBgUrl || artwork.noBgNoTrimUrl;
    if (noBgUrl) {
      const imgNoBg = new Image();
      imgNoBg.src = noBgUrl;
      imgNoBg.onload = () => setNoBgImage(imgNoBg);
      imgNoBg.onerror = () => {
        console.error("Failed to load no-background image");
        setNoBgImage(null);
      };
    }
  }, [artwork]);

  // Return the appropriate image based on preference
  const imageUrl = backgroundPreference
    ? artwork?.withBgUrl || artwork?.originalImageUrl
    : artwork?.noBgUrl || artwork?.noBgNoTrimUrl || artwork?.withBgUrl || artwork?.originalImageUrl;

  // Check loading state
  const isLoading = selectedSnapshot?.hasTag?.("loading") || false;

  // Get file for preview during upload
  const file = selectedSnapshot?.context.file;
  const originalImageUrl = selectedSnapshot?.context.artwork?.originalImageUrl;
  const imagePreview = file ? toFileObject(file, originalImageUrl) : undefined;

  const hasImage = Boolean(imageUrl || imagePreview);

  return {
    imageUrl,
    hasImage,
    artwork: selectedSnapshot?.context.artwork,
    isLoading,
    imagePreview,
    startTime: selectedSnapshot?.context.startTime,
    formControl: selectedSnapshot?.context.formControl,
    artworkId: selectedSnapshot?.context.artworkId,
    withBgImage,
    noBgImage,
    backgroundPreference
  };
}
